import type { JiraVersionUpdatedWarningConfigInput } from '@atlassian/jira-relay/src/__generated__/ui_ManageWarningsModalVersionWarningConfigMutation.graphql';
import type { JiraVersionWarningConfigState } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesManageWarnings_ManageWarningsModal.graphql';
import { QUERY_PARAM_WARNINGS } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/filter-sorting-query-param/constants.tsx';
import { useFilterSortingQueryParams } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/filter-sorting-query-param/index.tsx';
import { isWarningsFilterValue } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/filter-sorting-query-param/types.tsx';

type WarningConfigValues = {
	readonly openPullRequest: JiraVersionWarningConfigState;
	readonly openReview: JiraVersionWarningConfigState;
	readonly unreviewedCode: JiraVersionWarningConfigState;
	readonly failingBuild: JiraVersionWarningConfigState;
};

const booleanToState = (value: boolean): JiraVersionWarningConfigState =>
	value ? 'ENABLED' : 'DISABLED';

export const convertStatusToBoolean = ({
	openPullRequest,
	openReview,
	unreviewedCode,
	failingBuild,
}: WarningConfigValues): JiraVersionUpdatedWarningConfigInput => ({
	isOpenPullRequestEnabled: openPullRequest !== 'DISABLED',
	isOpenReviewEnabled: openReview !== 'DISABLED',
	isUnreviewedCodeEnabled: unreviewedCode !== 'DISABLED',
	isFailingBuildEnabled: failingBuild !== 'DISABLED',
});

export const getNewWarningConfigValues = (
	updatedBooleanValues: JiraVersionUpdatedWarningConfigInput,
	originalValues: WarningConfigValues,
): WarningConfigValues => ({
	openPullRequest:
		originalValues.openPullRequest === 'NOT_APPLICABLE'
			? 'NOT_APPLICABLE'
			: booleanToState(updatedBooleanValues.isOpenPullRequestEnabled ?? true),
	openReview:
		originalValues.openReview === 'NOT_APPLICABLE'
			? 'NOT_APPLICABLE'
			: booleanToState(updatedBooleanValues.isOpenReviewEnabled ?? true),
	unreviewedCode:
		originalValues.unreviewedCode === 'NOT_APPLICABLE'
			? 'NOT_APPLICABLE'
			: booleanToState(updatedBooleanValues.isUnreviewedCodeEnabled ?? true),
	failingBuild:
		originalValues.failingBuild === 'NOT_APPLICABLE'
			? 'NOT_APPLICABLE'
			: booleanToState(updatedBooleanValues.isFailingBuildEnabled ?? true),
});

export const useRemoveDisabledWarningFilters = ({
	isFailingBuildEnabled,
	isOpenPullRequestEnabled,
	isOpenReviewEnabled,
	isUnreviewedCodeEnabled,
}: JiraVersionUpdatedWarningConfigInput) => {
	const { parsedQueryParams, setWarningsFilterParam } = useFilterSortingQueryParams();

	return (): void => {
		const currentlySelectedWarningFilters = parsedQueryParams[QUERY_PARAM_WARNINGS] ?? [];
		const result = [];

		if (isFailingBuildEnabled && currentlySelectedWarningFilters.includes('FAILING_BUILD'))
			result.push('FAILING_BUILD');
		if (isOpenPullRequestEnabled && currentlySelectedWarningFilters.includes('OPEN_PULL_REQUEST'))
			result.push('OPEN_PULL_REQUEST');
		if (isUnreviewedCodeEnabled && currentlySelectedWarningFilters.includes('UNREVIEWED_CODE'))
			result.push('UNREVIEWED_CODE');
		if (isOpenReviewEnabled && currentlySelectedWarningFilters.includes('OPEN_REVIEW'))
			result.push('OPEN_REVIEW');

		setWarningsFilterParam(result.filter(isWarningsFilterValue));
	};
};
